import React, {useEffect, useState} from "react";
import { Card, Row, Empty,  Tag, Col, Space, Layout, Divider, Typography } from "antd";
import NivoChordChart from "../../charts/nivo/NivoChordChart";
import NivoHeatChart from "../../charts/nivo/NivoHeatChart";
import {
    extractEtfHoldingsLinks,
    transformDataForChord,
    findTopOverlappedTickers,
    computeOverlapMatrix,
    transformMatrixToHeatmapData,
    findEtfWithMostConnections,
    findEtfWithMostUnique
} from "./utils";
import {useEnsureTickersLoaded} from "../../hooks/useEnsureTickersLoaded";
import TickerChip from "../../Components/TickerChip";
import {useSelector} from "react-redux";
import {getTickersBySymbols} from "../../Store/slices/globalResources.slice";
import isEqual from 'lodash/isEqual';
import {useDeepMemo} from "../../hooks/useDeepMemo";

const { Link } = Typography;

/**
 *
 * @param parents {Object<string, {holdings: Array<{symbol: string, wgt: number}>}>} - An object with ETF names as
 *          ....keys and an array of holdings as values
 * @returns {JSX.Element}
 * @constructor
 */
const XrayChord = ({ parents }) => {
  const [topTickers, setTopTickers] = useState([]);
  const [heatData, setHeatData] = useState([]);
  const [mostUnique, setMostUnique] = useState(null);
  const [mostConnected, setMostConnected] = useState(null);
  const [stockLimit, setStockLimit] = useState(30);


  const memoizedParents = useDeepMemo(parents, [parents]);

  const { tickers, loading } = useEnsureTickersLoaded(topTickers); // Updated to use topTickers
  const etfTickerObjects = useSelector(state => getTickersBySymbols(state, Object.keys(parents)));
  const [chartDimensions, setChartDimensions] = useState({ width: 0, height: 0 });


  useEffect(() => {
    const links = extractEtfHoldingsLinks(memoizedParents, stockLimit);
    const matrix = computeOverlapMatrix(links);
    const keys = Object.keys(memoizedParents);
    const newHeatmapData = transformMatrixToHeatmapData(matrix, keys);
    const newTopTickers = findTopOverlappedTickers(links, 10).map(x => x.ticker);
    const newMostUnique = findEtfWithMostUnique(links);
    const newMostConnected = findEtfWithMostConnections(links);

    if (!isEqual(heatData, newHeatmapData)) setHeatData(newHeatmapData);
    if (!isEqual(topTickers, newTopTickers)) setTopTickers(newTopTickers);
    if (!isEqual(mostUnique, newMostUnique)) setMostUnique(newMostUnique);
    if (!isEqual(mostConnected, newMostConnected)) setMostConnected(newMostConnected);
  }, [memoizedParents, stockLimit]); // Only re-run effect if parents actually change

    useEffect(() => {
        const handleResize = () => {
            setChartDimensions({
                width: window.innerWidth * 0.7,
                height: window.innerHeight * 0.8
            });
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);


  if (loading) {
    return <Empty />;
  }

  const chordData = parents ? transformDataForChord(extractEtfHoldingsLinks(parents, stockLimit)) : null;

  const statItems = [
      {k: "ETFs", v: <Space>{Object.keys(parents).map(x=><Link>{x.toUpperCase()}</Link>)}</Space>},
      {k: "ETF With Most Overlap", v: mostConnected ?<Tag bordered={false} color={'geekblue-inverse'}>{mostConnected?.key?.toUpperCase()} ({mostConnected.value})</Tag> : 'N/A'},
      {k: "ETF With Most Unique Stocks", v: mostUnique ? <Tag bordered={false} color={'geekblue-inverse'}>{mostUnique?.key?.toUpperCase()} ({mostUnique.value})</Tag> : 'N/A'},
      {k: "Stocks Included", v: chordData?.keys?.length},

  ]

  return (
    <Layout style={{padding: '2.5%'}}>
        <Layout.Sider width={300} style={{background: "#fff"}} className={'px-3'}>
            <Space direction={'vertical'}>
                <Typography.Text strong style={{fontWeight: '700'}}>Stocks With Most Concentration</Typography.Text>
                <Divider/>
                {tickers.map((ticker) => (
                    <TickerChip key={ticker} ticker={ticker}/>
                ))}
            </Space>
        </Layout.Sider>
        <Layout.Content className={'sunken-shadow px-6'}>
            <Card bordered={false} style={{background: "transparent"}}>
                <Row>
                    {statItems.map((item) => (
                        <Col span={6}>
                            {item.k} {item.v}
                        </Col>
                    ))}
                </Row>
                <Divider/>
                <div style={{height: `${chartDimensions.height}px`, width: `${chartDimensions.width}px`, margin: 'auto'}}>
                    <NivoChordChart
                        {...chordData}
                        legend={false}
                    />
                </div>
            </Card>
        </Layout.Content>
    </Layout>
  );
};
export default XrayChord;